import { useEffect, useRef, useState } from 'react';
import { REVIEWS, BUSINESS_STATS } from './data.js';
import { getGoogleAggregate } from './google-reviews.js';

// ─────────────────────────────────────────────────────────────────────
// TrustLuxe — premium trust panel used on all hero contexts.
// Editorial layout: 4 cells (rating · response · clients · 24/7),
// serif numerals, gold/orange duotone, count-up on scroll.
// ─────────────────────────────────────────────────────────────────────
export function TrustLuxe({ cityName }) {
  const ref = useRef(null);
  // Live Google-aggregaat zodat het "X reviews · 4,6 gem." onderschrift
  // mee groeit met de werkelijke Google-stand (was eerder hardcoded).
  const [agg, setAgg] = useState({ rating: REVIEWS.rating, total: REVIEWS.total });
  useEffect(() => {
    let alive = true;
    getGoogleAggregate().then(data => {
      if (!alive || !data) return;
      if (data.rating !== agg.rating || data.total !== agg.total) {
        setAgg({ rating: data.rating, total: data.total });
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ratingStr = String(agg.rating).replace('.', ',');

  const eyebrow = cityName
    ? `Vertrouwd door ${cityName} sinds 2017`
    : "Vertrouwd door de Randstad sinds 2017";
  const reactionLabel = cityName
    ? `Reactietijd in ${cityName}`
    : "Gemiddelde reactietijd";
  const reactionMeta = cityName
    ? `${cityName} en omliggende gemeenten`
    : "Heel Zuid-Holland · Noord-Holland · Utrecht";

  useEffect(() => {
    const panel = ref.current;
    if (!panel) return;
    let cancelled = false;

    const ratingEl  = panel.querySelector('[data-anim="rating"]');
    const starsEl   = panel.querySelector('[data-anim="stars"]');
    const clientsEl = panel.querySelector('[data-anim="clients"]');
    const featured  = panel.querySelector('.tl-cell-featured');
    if (!ratingEl || !clientsEl || !featured) return;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const animate = (duration, onTick) => {
      const t0 = performance.now();
      const step = (now) => {
        if (cancelled) return;
        const p = Math.min(1, (now - t0) / duration);
        onTick(easeOutCubic(p), p);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    // Hero-cijfer = cumulatief sinds oprichting (TrustLuxe context: "in de Randstad").
    const clientsTarget = BUSINESS_STATS.customersAllTime;
    const run = () => {
      // Rating-target uit live aggregaat (was hardcoded 4.6)
      const ratingTarget = agg.rating || 4.6;
      animate(1500, (e) => {
        const v = e * ratingTarget;
        ratingEl.textContent = v.toFixed(1).replace('.', ',');
        if (starsEl) starsEl.style.setProperty('--w', (e * (ratingTarget / 5 * 100)) + '%');
        if (v >= 4.55) featured.classList.add('is-gold');
        else featured.classList.remove('is-gold');
      });
      animate(1700, (e) => {
        clientsEl.textContent = Math.round(e * clientsTarget).toLocaleString('nl-NL');
      });
    };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          run();
          obs.disconnect();
        }
      });
    }, { threshold: 0.25 });
    obs.observe(panel);

    return () => { cancelled = true; obs.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityName, agg.rating]);

  return (
    <div
      className="trust-luxe"
      role="region"
      aria-label={`Vertrouwen ${cityName || 'in de Randstad'}`}
      ref={ref}
    >
      <div className="tl-eyebrow">
        <span className="tl-rule" aria-hidden="true"></span>
        <span className="tl-label">{eyebrow}</span>
        <span className="tl-rule" aria-hidden="true"></span>
      </div>

      <div className="tl-grid">
        {/* Rating — featured cell */}
        <div className="tl-cell tl-cell-featured">
          <div className="tl-mark" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </div>
          <div className="tl-num">
            {/* Server-side initial value uit data.js zodat Google bij eerste
                paint correcte cijfers ziet. Animatie overschrijft daarna
                client-side maar startwaarde is al de echte. */}
            <span className="tl-num-i" data-anim="rating">{ratingStr}</span>
            <span className="tl-num-frac">/5</span>
          </div>
          <div className="tl-stars" aria-hidden="true">
            <span data-anim="stars" style={{ '--w': `${(agg.rating / 5) * 100}%` }}></span>
          </div>
          <div className="tl-label-l">Google rating</div>
          <div className="tl-meta">{agg.total} reviews · {ratingStr} gem.</div>
        </div>

        <div className="tl-div" aria-hidden="true"></div>

        {/* Response time */}
        <div className="tl-cell">
          <div className="tl-mark" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="13" r="8"/>
              <path d="M12 9v4l2 2"/>
            </svg>
            <span className="tl-pulse"></span>
          </div>
          <div className="tl-num">
            <span className="tl-num-pre">≤</span>
            <span className="tl-num-i">24</span>
            <span className="tl-num-suf">u</span>
          </div>
          <div className="tl-label-l">{reactionLabel}</div>
          <div className="tl-meta">{reactionMeta}</div>
        </div>

        <div className="tl-div" aria-hidden="true"></div>

        {/* Clients */}
        <div className="tl-cell">
          <div className="tl-mark" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="tl-num">
            <span className="tl-num-i" data-anim="clients">{BUSINESS_STATS.customersAllTime.toLocaleString('nl-NL')}</span>
            <span className="tl-num-suf">+</span>
          </div>
          <div className="tl-label-l">Tevreden klanten in de Randstad</div>
          <div className="tl-meta">Particulier · Zakelijk · Horeca · VvE</div>
        </div>

        <div className="tl-div" aria-hidden="true"></div>

        {/* 24/7 */}
        <div className="tl-cell">
          <div className="tl-mark" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="m9 12 2 2 4-4"/>
            </svg>
          </div>
          <div className="tl-num">
            <span className="tl-num-i">24/7</span>
          </div>
          <div className="tl-label-l">Spoedhulp · live bereikbaar</div>
          <div className="tl-meta tl-meta-live">
            <span className="tl-live-dot"></span>
            <span className="tl-tel-label">Telefoon open</span>
            <span className="tl-sep"> · </span>
            <span className="tl-tel">085 500 50 50</span>
          </div>
        </div>
      </div>

      <div className="tl-footer">
        <span className="tl-badge">Gecertificeerde partners</span>
        <span className="tl-dot" aria-hidden="true"></span>
        <span className="tl-badge">Vaste prijs vooraf</span>
        <span className="tl-dot" aria-hidden="true"></span>
        <span className="tl-badge">Vakkundige nacontrole</span>
      </div>
    </div>
  );
}
